import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { surveysApi } from '../../survey/surveyApi';
import type { Survey } from '../../survey/surveyTypes';

const ID = 'development-plan-survey-picker';

/**
 * Create development plan, step zero — WHICH SURVEY (product call, 12 Oct 2026).
 *
 * A plan is created for a survey's assessed employees, and Survey › Development Plan's own Create
 * already knows its survey. This register spans every survey, so it asks first, then hands over to
 * the SAME two-step form — one create path, not a second form that can drift.
 *
 * It WRITES NOTHING, so there is no confirm tick and its action is Next: the form that follows
 * carries the tick. Only live surveys are offered — a deleted survey takes no new plan.
 */
export function DevelopmentPlanSurveyPickerModal({
  onClose,
  onPicked,
}: {
  onClose: () => void;
  onPicked: (surveyId: string) => void;
}) {
  const [surveys, setSurveys] = useState<Survey[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [surveyId, setSurveyId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    surveysApi
      .list({ status: 'active', pageSize: 100, sort: 'title:asc' }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setSurveys(page.items);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the surveys.', 0));
      });

    return () => controller.abort();
  }, []);

  return (
    <Modal
      open
      onClose={onClose}
      title="Create development plan"
      size="lg"
      id={`${ID}-modal`}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button id={`${ID}-next`} variant="primary" onClick={() => surveyId && onPicked(surveyId)} disabled={!surveyId}>
            Next
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id={`${ID}-survey`}
          label="Survey"
          required
          description="The survey whose assessed employees the plan is for. The plan and the employees come next."
          placeholder="Choose a survey"
          value={surveyId}
          onChange={setSurveyId}
          options={(surveys ?? []).map((s) => ({ value: s.id, label: s.title }))}
          loading={surveys === null && error === null}
          clearable={false}
          error={surveys !== null && surveys.length === 0 ? 'There is no survey yet. Create one under Survey first.' : undefined}
        />
      </div>
    </Modal>
  );
}
